import { parse } from 'node-html-parser';
import DOMPurify from 'isomorphic-dompurify';
import { AnchorBehavior } from '../organisms/LegalConsents/LegalConsents.types';

interface Props {
  label: string;
  anchorBehavior: AnchorBehavior;
  ariaWindowText: string | null;
  ariaModalText: string | null;
}
export function getEnhancedLinksForNewTab({ label, ariaWindowText }: Props) {
  const root = parse(label);
  const links = root.querySelectorAll('a');
  links.forEach(link => {
    link.setAttribute('target', '_blank');
    link.removeAttribute('data-is-modal');

    /* Required by the RichText component and Marriott global styles.
     * The anchor class (m-link-tertiary-button-external) enables the
     * external-link presentation, and the appended m-link-action span is used
     * by the RichText CSS to render the external-link indicator correctly.
     */
    link.classList.add('m-link-tertiary-button-external');
    // Avoid adding multiple spans if already added
    const alreadyHasNote = link.querySelector('span.m-link-action');
    if (!alreadyHasNote) {
      const sanitizedAriaWindowText = DOMPurify.sanitize(ariaWindowText ?? '');
      link.insertAdjacentHTML(
        'beforeend',
        `<span class="m-link-action" aria-atomic="true" aria-label="${sanitizedAriaWindowText}"></span>`
      );
    }
  });

  return root.toString();
}

export function getEnhancedLinksForDefault({ label, ariaModalText, ariaWindowText }: Props) {
  const root = parse(label);
  const links = root.querySelectorAll('a');
  links.forEach(link => {
    const isModal = link.getAttribute('data-is-modal') === 'true';
    const isNewWindow = link.getAttribute('target') === '_blank';
    if (!isModal && isNewWindow) {
      link.classList.add('m-link-tertiary-button-external');
    }
    // Avoid adding multiple spans if already added
    const alreadyHasNote = link.querySelector('span.m-link-action, span.d-inline-block');
    if (!alreadyHasNote) {
      const text = isModal ? ariaModalText : ariaWindowText;
      const sanitizedAriaLabelText = DOMPurify.sanitize(text ?? '');
      const spanClassName = !isModal && isNewWindow ? 'm-link-action' : 'd-inline-block';
      link.insertAdjacentHTML(
        'beforeend',
        `<span class="${spanClassName}" aria-atomic="true" aria-label="${sanitizedAriaLabelText}"></span>`
      );
    }
  });

  return root.toString();
}

export function getEnhancedLabel(props: Props) {
  return props.anchorBehavior === 'newTab'
    ? getEnhancedLinksForNewTab({ ...props })
    : getEnhancedLinksForDefault({ ...props });
}
